import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User, History } from 'lucide-react';
import { MemberInfosTab } from './MemberInfosTab';
import { MemberHistoryTab } from './MemberHistoryTab';

// Type du membre (repris de Team.tsx)
interface Membre {
  id: string;
  prenom: string;
  nom: string;
  poste: string;
  specialite: string;
  taux_horaire: number;
  charges_patronales: number;
  coefficient_charges: number;
  heures_par_jour: number;
  statut: 'sur_chantier' | 'disponible' | 'repos' | 'en_arret' | 'en_rotation';
  equipe_id?: string | null;
  date_fin_statut?: string | null;
}

interface MemberDetailModalProps {
  membre: Membre | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Infos supplémentaires pour l'affichage
  equipeNom?: string;
  affectationActive?: {
    chantier: string;
    dateFin: string;
    isRetard: boolean;
  };
}

/**
 * Modal de détail d'un membre avec onglets Informations et Historique
 * Lecture seule - pour éditer, utiliser le bouton crayon
 */
export function MemberDetailModal({
  membre,
  open,
  onOpenChange,
  equipeNom,
  affectationActive,
}: MemberDetailModalProps) {
  if (!membre) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="industrial" className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            <span>
              {membre.prenom} {membre.nom}
            </span>
            <span className="text-muted-foreground font-normal text-sm ml-2">
              — {membre.poste}
            </span>
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="infos" className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="infos" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Informations
            </TabsTrigger>
            <TabsTrigger value="historique" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Historique
            </TabsTrigger>
          </TabsList>

          <TabsContent value="infos" className="flex-1 overflow-auto">
            <MemberInfosTab
              membre={membre}
              equipeNom={equipeNom}
              affectationActive={affectationActive}
            />
          </TabsContent>

          <TabsContent value="historique" className="flex-1 overflow-auto min-h-0">
            <MemberHistoryTab
              membreId={membre.id}
              tauxHoraire={membre.taux_horaire}
              coefficientCharges={membre.coefficient_charges || 1.85}
              heuresParJour={membre.heures_par_jour || 8}
              prenom={membre.prenom}
              nom={membre.nom}
              poste={membre.poste}
              specialite={membre.specialite}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
